import { forwardRef } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';

/**
 * Centered “hero” toast: large status glyph, bold title, secondary detail (reference layout).
 * Forwards ref to the root Paper so Snackbar/Fade transitions can attach a DOM node (reflow).
 */
const StackedToast = forwardRef(function StackedToast({ severity = 'success', title, message, onClose }, ref) {
  const theme = useTheme();
  const isSuccess = severity === 'success';
  const displayTitle =
    title || (isSuccess ? 'Success' : 'Something went wrong');
  const accent = isSuccess ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Paper
      ref={ref}
      elevation={12}
      sx={{
        position: 'relative',
        textAlign: 'center',
        px: { xs: 3, sm: 5 },
        py: { xs: 3.5, sm: 4 },
        borderRadius: 2,
        maxWidth: 520,
        width: '100%',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      {onClose && (
        <IconButton
          size="small"
          aria-label="Dismiss"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseOutlined />
        </IconButton>
      )}

      <Box
        sx={(t) => ({
          width: { xs: 72, sm: 80 },
          height: { xs: 72, sm: 80 },
          borderRadius: '50%',
          border: '3px solid',
          borderColor: isSuccess ? 'success.main' : 'error.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2.5,
          bgcolor: isSuccess ? alpha(t.palette.success.main, 0.12) : alpha(t.palette.error.main, 0.12)
        })}
      >
        {isSuccess ? (
          <CheckOutlined style={{ fontSize: 40, color: accent }} />
        ) : (
          <ExclamationCircleOutlined style={{ fontSize: 38, color: accent }} />
        )}
      </Box>

      <Typography
        component="div"
        variant="h6"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          letterSpacing: '-0.01em',
          lineHeight: 1.3,
          px: 1
        }}
      >
        {displayTitle}
      </Typography>

      {message ? (
        <Typography
          component="div"
          variant="body2"
          sx={{
            mt: 1.25,
            color: 'text.secondary',
            lineHeight: 1.5,
            px: 1
          }}
        >
          {message}
        </Typography>
      ) : null}
    </Paper>
  );
});

export default StackedToast;
